<!-- Hero Section with Filters -->
<section class="hero">
  <div class="container">
    <h1 class="hero__title">{{ 'pages.marketAnalytics.title' | transloco }}</h1>
    <p class="hero__description">{{ 'pages.marketAnalytics.description' | transloco }}</p>

    <!-- Filter Inputs -->
    <form [formGroup]="filterForm" class="filter-box">
      <!-- Row 1: Date Range and Period -->
      <div class="filter-box__row">
        <div class="filter-box__input">
          <i class="fas fa-arrow-right-from-bracket"></i>
          <input type="date" class="form-control" formControlName="dateFrom" />
        </div>
        <div class="filter-box__input">
          <i class="fas fa-arrow-right-to-bracket"></i>
          <input type="date" class="form-control" formControlName="dateTo" />
        </div>
        <div class="filter-box__input">
          <i class="fas fa-clock"></i>
          <select class="form-control" formControlName="period">
            <option *ngFor="let period of periods" [value]="period.value">
              {{ period.label | transloco }}
            </option>
          </select>
        </div>
      </div>

      <!-- Row 2: Location Filters -->
      <div class="filter-box__row">
        <div class="filter-box__input">
          <app-input
            type="text"
            [placeholder]="'analytics.city' | transloco"
            [hasIcon]="true"
            iconPosition="left"
            [showClearButton]="true"
            class="input-large"
            formControlName="city">
            <i class="fas fa-map-marker-alt" slot="icon-left"></i>
          </app-input>
        </div>
        <div class="filter-box__input">
          <app-input
            type="text"
            [placeholder]="'analytics.country' | transloco"
            [hasIcon]="true"
            iconPosition="left"
            [showClearButton]="true"
            class="input-large"
            formControlName="country">
            <i class="fas fa-globe" slot="icon-left"></i>
          </app-input>
        </div>
      </div>

      <!-- Quick Actions & Apply Button -->
      <div class="filter-box__actions">
        <div class="quick-filters">
          <button type="button" mat-button (click)="setLast7Days()">
            {{ 'analytics.last7Days' | transloco }}
          </button>
          <button type="button" mat-button (click)="setLast30Days()">
            {{ 'analytics.last30Days' | transloco }}
          </button>
          <button type="button" mat-button (click)="setLast90Days()">
            {{ 'analytics.last90Days' | transloco }}
          </button>
        </div>
        <div class="main-actions">
          <button type="button" class="filter-box__button filter-box__button--secondary" (click)="resetFilters()">
            <i class="fas fa-redo"></i>
            {{ 'analytics.reset' | transloco }}
          </button>
          <button type="button" class="filter-box__button" (click)="applyFilters()">
            <i class="fas fa-search"></i>
            {{ 'analytics.applyFilters' | transloco }}
          </button>
        </div>
      </div>
    </form>
  </div>
</section>

<!-- Analytics Content Section -->
<section class="analytics-content">
  <div class="container">
    <!-- Analytics Tabs -->
    <mat-tab-group [(selectedIndex)]="selectedTabIndex" class="analytics-tabs" mat-stretch-tabs="false" animationDuration="300ms">
      <!-- Tab 1: Available Jobs -->
      <mat-tab [label]="'analytics.tabAvailableJobs' | transloco">
        <ng-template matTabLabel>
          <i class="fas fa-briefcase tab-icon"></i>
          <span class="tab-label">{{ 'analytics.tabAvailableJobs' | transloco }}</span>
        </ng-template>
        <ng-template matTabContent>
          <div class="tab-content">
            <app-available-jobs-chart [filters]="filters" [legendPosition]="legendPosition"></app-available-jobs-chart>
          </div>
        </ng-template>
      </mat-tab>

      <!-- Tab 2: Available Candidates -->
      <mat-tab [label]="'analytics.tabAvailableCandidates' | transloco">
        <ng-template matTabLabel>
          <i class="fas fa-users tab-icon"></i>
          <span class="tab-label">{{ 'analytics.tabAvailableCandidates' | transloco }}</span>
        </ng-template>
        <ng-template matTabContent>
          <div class="tab-content">
            <app-available-candidates-chart [filters]="filters" [legendPosition]="legendPosition"></app-available-candidates-chart>
          </div>
        </ng-template>
      </mat-tab>

      <!-- Tab 3: Created Jobs -->
      <mat-tab [label]="'analytics.tabCreatedJobs' | transloco">
        <ng-template matTabLabel>
          <i class="fas fa-plus-circle tab-icon"></i>
          <span class="tab-label">{{ 'analytics.tabCreatedJobs' | transloco }}</span>
        </ng-template>
        <ng-template matTabContent>
          <div class="tab-content">
            <app-created-jobs-chart [filters]="filters" [legendPosition]="legendPosition"></app-created-jobs-chart>
          </div>
        </ng-template>
      </mat-tab>

      <!-- Tab 4: Registered Candidates -->
      <mat-tab [label]="'analytics.tabRegisteredCandidates' | transloco">
        <ng-template matTabLabel>
          <i class="fas fa-user-plus tab-icon"></i>
          <span class="tab-label">{{ 'analytics.tabRegisteredCandidates' | transloco }}</span>
        </ng-template>
        <ng-template matTabContent>
          <div class="tab-content">
            <app-registered-candidates-chart [filters]="filters" [legendPosition]="legendPosition"></app-registered-candidates-chart>
          </div>
        </ng-template>
      </mat-tab>

      <!-- Tab 5: Applications -->
      <mat-tab [label]="'analytics.tabApplications' | transloco">
        <ng-template matTabLabel>
          <i class="fas fa-clipboard-list tab-icon"></i>
          <span class="tab-label">{{ 'analytics.tabApplications' | transloco }}</span>
        </ng-template>
        <ng-template matTabContent>
          <div class="tab-content">
            <app-applications-chart [filters]="filters" [legendPosition]="legendPosition"></app-applications-chart>
          </div>
        </ng-template>
      </mat-tab>

      <!-- Tab 6: Hiring Rate -->
      <mat-tab [label]="'analytics.tabHiringRate' | transloco">
        <ng-template matTabLabel>
          <i class="fas fa-arrow-up tab-icon"></i>
          <span class="tab-label">{{ 'analytics.tabHiringRate' | transloco }}</span>
        </ng-template>
        <ng-template matTabContent>
          <div class="tab-content">
            <app-hiring-rate-chart [filters]="filters" [legendPosition]="legendPosition"></app-hiring-rate-chart>
          </div>
        </ng-template>
      </mat-tab>

      <!-- Tab 7: Supply vs Demand -->
      <mat-tab [label]="'analytics.tabSupplyDemand' | transloco">
        <ng-template matTabLabel>
          <i class="fas fa-balance-scale tab-icon"></i>
          <span class="tab-label">{{ 'analytics.tabSupplyDemand' | transloco }}</span>
        </ng-template>
        <ng-template matTabContent>
          <div class="tab-content">
            <app-supply-demand-chart [filters]="filters"></app-supply-demand-chart>
          </div>
        </ng-template>
      </mat-tab>

      <!-- Tab 8: Market Summary -->
      <mat-tab [label]="'analytics.tabMarketSummary' | transloco">
        <ng-template matTabLabel>
          <i class="fas fa-tachometer-alt tab-icon"></i>
          <span class="tab-label">{{ 'analytics.tabMarketSummary' | transloco }}</span>
        </ng-template>
        <ng-template matTabContent>
          <div class="tab-content">
            <app-market-summary-dashboard [filters]="filters"></app-market-summary-dashboard>
          </div>
        </ng-template>
      </mat-tab>
    </mat-tab-group>
  </div>
</section>
